import React from "react";
import { fetchEpiCapture, fetchEpiDocument } from "../../lib/assistantApi.js";
import { epiQuestionsText, keepEpiPunctuation, parseEpiAnswer, splitEpiBold } from "./epiAnswerBlocks.js";

const BUTTON = "h-7 rounded-[4px] border border-slate-300 bg-white px-2 py-0 text-xs font-semibold text-slate-700 hover:border-slate-400 hover:bg-slate-50 hover:text-slate-900 focus-visible:outline-accent disabled:border-slate-200 disabled:bg-slate-50 disabled:text-slate-400 disabled:opacity-100 disabled:shadow-none";

function EpiInline({ text }) {
  return splitEpiBold(keepEpiPunctuation(text)).map((part, index) => (
    part.bold ? <strong key={index}>{part.text}</strong> : <React.Fragment key={index}>{part.text}</React.Fragment>
  ));
}

function EpiImage({ block }) {
  const [url, setUrl] = React.useState(null);
  const [error, setError] = React.useState("");
  React.useEffect(() => {
    let active = true;
    let objectUrl;
    fetchEpiCapture(`/api/assistant/captures/${encodeURIComponent(block.id)}.png`).then((value) => {
      objectUrl = value;
      if (active) setUrl(value);
      else URL.revokeObjectURL(value);
    }).catch((failure) => { if (active) setError(failure.message); });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [block.id]);
  return <figure className="epi-document__figure">
    {url ? <img alt={block.caption} onLoad={(event) => { event.currentTarget.style.width = `${event.currentTarget.naturalWidth / 2}px`; }} src={url} /> : <p role={error ? "alert" : "status"}>{error || "Chargement de l’illustration…"}</p>}
    <figcaption>{block.caption}</figcaption>
  </figure>;
}

function EpiBlocks({ blocks, lead = false }) {
  return blocks.map((block, index) => {
    if (block.type === "image") return <EpiImage block={block} key={index} />;
    if (block.type === "list") {
      const items = block.items.map((item, itemIndex) => <li key={itemIndex}><EpiInline text={item} /></li>);
      return block.ordered ? <ol className="epi-steps" key={index}>{items}</ol> : <ul key={index}>{items}</ul>;
    }
    if (block.type === "callout") return <p className={`epi-callout epi-callout--${block.tone}`} key={index}>{block.title ? <><strong>{block.title} :</strong> <EpiInline text={block.text} /></> : <span><EpiInline text={block.text} /></span>}</p>;
    if (block.type === "table") return <div className="epi-table-scroll" key={index}><table className="epi-table">
      <thead><tr>{block.header.map((cell, cellIndex) => <th key={cellIndex}><EpiInline text={cell} /></th>)}</tr></thead>
      <tbody>{block.rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}><EpiInline text={cell} /></td>)}</tr>)}</tbody>
    </table></div>;
    return <p className={lead && index === 0 ? "epi-lead" : undefined} key={index}><EpiInline text={block.text} /></p>;
  });
}

export function EpiAnswer({ text, muted = false }) {
  return <div className={muted ? "epi-answer epi-answer--muted" : "epi-answer"} data-epi-answer="true"><EpiBlocks blocks={parseEpiAnswer(text)} lead /></div>;
}

export function saveEpiFile({ blob, filename }) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function EpiDownloads({ questionId }) {
  const [pending, setPending] = React.useState("");
  const [error, setError] = React.useState("");
  const download = async (format) => {
    setPending(format);
    setError("");
    try {
      saveEpiFile(await fetchEpiDocument(questionId, format));
    } catch (failure) { setError(failure.message); }
    finally { setPending(""); }
  };
  return <div className="epi-downloads">
    <div className="epi-downloads__buttons">{[["pdf", "PDF"], ["docx", "Word"]].map(([format, label]) => (
      <button className={BUTTON} data-epi-download-format={format} disabled={Boolean(pending)} key={format} onClick={() => download(format)} type="button">
        {pending === format ? "Préparation…" : `Télécharger en ${label}`}
      </button>
    ))}</div>
    {error ? <p className="epi-error" role="alert">{error}</p> : null}
  </div>;
}

// Page de garde d'un guide : objectif, étapes avec leur écran, encadrés « Avant de commencer » et notes.
function EpiCover({ cover }) {
  return <section className="epi-document__cover">
    {cover.objective ? <p><EpiInline text={cover.objective} /></p> : null}
    <h4>{cover.heading}</h4>
    <ol className="epi-document__steps">{cover.steps.map((step, index) => <li key={index}>
      <span className="epi-document__number">{step.number}</span>
      <span><strong><EpiInline text={step.title} /></strong>{step.where ? <> · <EpiInline text={step.where} /></> : null}</span>
    </li>)}</ol>
    {cover.boxes.map((box, index) => <div className="epi-callout" key={index}>
      <strong>{box.title}</strong>
      {box.note ? <p><EpiInline text={box.note} /></p> : null}
      {box.items.length ? <ul>{box.items.map((item, itemIndex) => <li key={itemIndex}><EpiInline text={item} /></li>)}</ul> : null}
    </div>)}
    {cover.notes.map((note, index) => <p key={index}><EpiInline text={note} /></p>)}
  </section>;
}

export default function EpiDocument({ document, questionId }) {
  return <article className="epi-document" data-epi-document="true">
    <h3>{document.title}</h3>
    <EpiDownloads questionId={questionId} />
    {document.cover ? <EpiCover cover={document.cover} /> : null}
    {document.chapters.map((chapter, index) => <section className="epi-document__chapter" key={index}>
      {chapter.kicker ? <p className="epi-document__kicker">{chapter.kicker}</p> : null}
      <h4>{chapter.title}</h4>
      <div className="epi-answer"><EpiBlocks blocks={chapter.blocks} /></div>
      {chapter.questions?.length ? <p className="epi-document__questions">{epiQuestionsText(chapter.questions)}</p> : null}
      {(document.version ?? 1) === 1 ? <p className="epi-document__sources">Sources : {chapter.sources.map((source) => source.title).join(" ; ")}</p> : null}
    </section>)}
    {document.glossary?.length ? <section className="epi-document__chapter">
      <h4>Lexique</h4>
      <dl className="epi-document__glossary">{document.glossary.map((entry) => <div key={entry.term}>
        <dt>{entry.term}</dt><dd><EpiInline text={entry.definition} /></dd>
      </div>)}</dl>
    </section> : null}
  </article>;
}
